import { createContext, type ReactNode, useContext, useEffect } from "react";

import { areObjectsEqual } from "features/templates/utils";

import useConversation from "../datahooks/useConversation";
import useActiveConversationState from "../hooks/useActiveConversationState";
import { useNewConversation } from "../hooks/useNewConversation";

import type { Conversation } from "../types";

const ActiveConversationContext = createContext<Conversation | null>(null);

export function ActiveConversationProvider({
  children,
  conversations,
}: {
  children: ReactNode;
  conversations: Conversation[] | undefined;
}) {
  const { newConversation } = useNewConversation();
  const { activeConversationState, setActiveConversationState } =
    useActiveConversationState();

  // this is the conversation found in the conversations list, it's always used as the active conversation if it exists
  const conversationListConversation =
    conversations && activeConversationState
      ? conversations.find(
          ({ conversation_urn, highlight, last_activity_at }) =>
            conversation_urn === activeConversationState.conversationUrn &&
            (activeConversationState.highlightContext && highlight
              ? areObjectsEqual(
                  activeConversationState.highlightContext.highlight,
                  highlight,
                ) &&
                activeConversationState.highlightContext.lastActivityAt ===
                  last_activity_at
              : true),
        )
      : undefined;

  // biome-ignore lint/suspicious/noImplicitAnyLet: types are inferred by TS
  let conversationData;

  if (activeConversationState) {
    conversationData = {
      accountId: activeConversationState.accountId,
      conversationUrn: activeConversationState.conversationUrn,
      initialConversation: conversationListConversation,
    };
  } else if (newConversation) {
    conversationData = {
      accountId: newConversation.owner,
      leadId: newConversation.participant.id,
    };
  }

  const { conversation } = useConversation(conversationData);

  useEffect(() => {
    if (conversation && !activeConversationState) {
      setActiveConversationState(
        conversation.conversation_urn,
        conversation.owner,
        undefined,
      );
    }
  }, [conversation]);

  return (
    <ActiveConversationContext.Provider
      value={conversationListConversation || conversation}
    >
      {children}
    </ActiveConversationContext.Provider>
  );
}
export function useActiveConversationContext() {
  const activeConversation = useContext(ActiveConversationContext);
  return { activeConversation };
}
